import React from 'react';
import PropTypes from 'prop-types';
import styled, { createGlobalStyle } from 'styled-components';
import { withRouteData } from 'react-static';

import Logo from '../logo/Logo';
import HeaderLinks from './HeaderLinks';
import LanguageSelector from './LanguageSelector';
import GetStartedCta from './GetStartedCta';
import HamburgerMenu from './HamburgerMenu';
import MobileMenuContent from './MobileMenuContent';
import FilteredCampaignBannerList from './FilteredCampaignBannerList';
import { media } from '../../../styles/style-utils';
import { type } from '../../../styles/typography';

export const HEADER_HEIGHT = 56;

const Wrapper = styled.div`
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 1000;

  .inapp-webview &,
  .ios-webview & {
    display: none !important;
  }
`;

const Bar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${HEADER_HEIGHT}px;
  padding-right: var(--b-gutter);
  background-color: var(--b-black);
  color: var(--b-white);
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 60px;
  min-width: 0;

  .headerLogo {
    display: flex;
    align-items: center;
    height: ${HEADER_HEIGHT}px;
    padding: 0 22px;

    .logoSvg {
      display: block;
      width: 100px;
      height: 16px;
    }
  }
`;

const DesktopNav = styled.nav`
  display: none;

  ${media.large`
    display: flex;
    align-items: center;
    gap: 29px;
  `};

  a {
    ${type('label', 'small', { emphasis: true, uppercase: true })};
    color: rgba(255, 255, 255, 0.72);
    text-align: center;
    transition: color 0.16s ease;

    &:hover,
    &.active {
      color: var(--b-white);
    }
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  .desktopOnly {
    display: none;

    ${media.large`
      display: block;
    `};
  }
`;

const MobileMenu = styled.div`
  display: flex;
  align-items: center;
  margin-left: 8px;

  ${media.large`
    display: none;
  `};

  > button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
  }

  .hamburger {
    width: 24px;
    height: 24px;
  }
`;

const GlobalStyle = createGlobalStyle`
  :root {
    --header-height: ${({ height }) => height}px;
  }
`;

class Header extends React.Component {
  state = { height: HEADER_HEIGHT };

  constructor(props) {
    super(props);
    this.wrapper = React.createRef();
  }

  componentDidMount() {
    this.measure();
    window.addEventListener('resize', this.measure);
    window.addEventListener('load', this.measure);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.measure);
    }
    this.timer = setTimeout(this.measure, 1000);
    if (typeof ResizeObserver !== 'undefined' && this.wrapper.current) {
      this.observer = new ResizeObserver(this.measure);
      this.observer.observe(this.wrapper.current);
      this.observed = this.wrapper.current;
    }
  }

  componentDidUpdate() {
    if (this.observer && this.wrapper.current && this.observed !== this.wrapper.current) {
      this.observer.disconnect();
      this.observer.observe(this.wrapper.current);
      this.observed = this.wrapper.current;
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.measure);
    window.removeEventListener('load', this.measure);
    clearTimeout(this.timer);
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  renderBar(basename) {
    return (
      <React.Fragment>
        <FilteredCampaignBannerList basename={basename} />
        <Bar id="headerWrapper">
          <Left>
            <Logo className="headerLogo" />
            <DesktopNav>
              <HeaderLinks />
            </DesktopNav>
          </Left>
          <Right>
            <LanguageSelector className="desktopOnly" />
            <GetStartedCta trackingData={{ from: 'Header' }} />
            <MobileMenu>
              <HamburgerMenu>
                <MobileMenuContent />
              </HamburgerMenu>
            </MobileMenu>
          </Right>
        </Bar>
      </React.Fragment>
    );
  }

  render() {
    const { basename } = this.props;
    return (
      <React.Fragment>
        <GlobalStyle height={this.state.height} />
        <Wrapper ref={this.wrapper}>{this.renderBar(basename)}</Wrapper>
      </React.Fragment>
    );
  }

  measure = () => {
    const el = this.wrapper.current;
    if (!el) return;
    const height = Math.round(el.getBoundingClientRect().height) || HEADER_HEIGHT;
    if (height !== this.state.height) {
      this.setState({ height });
    }
  };
}

Header.propTypes = {
  basename: PropTypes.string.isRequired,
};

export default withRouteData(Header);



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/main/header/Header.js